import { defineStore } from "pinia";

import { PAGE_ACCESS_KEYS } from "@/utils/routePermissions";
import { deleteCookie } from "@/utils/cookieUtils";
import { getCompanyId } from "@/utils/userUtils";

import permissionRolesApi from "@/services/api/permissionRolesApi";

const POLL_INTERVAL_MS = 240_000;

export const usePermissionsStore = defineStore("permissions", {
  state: () => ({
    pageAccess: {},
    features: {},
    isLoaded: false,
    _pollTimer: null,
  }),

  getters: {
    canSeePage: (state) => (pageKey) => {
      if (!state.isLoaded) return false;
      if (!getCompanyId()) return false;
      if (!(pageKey in state.pageAccess)) return true;
      return state.pageAccess[pageKey] === true;
    },
    featureEnabled: (state) => (featureKey) =>
      state.features[featureKey] === true,
  },

  actions: {
    async loadPermissions() {
      const companyId = getCompanyId();

      if (!companyId) {
        this.pageAccess = {};
        this.features = {};
        this.isLoaded = true;
        return;
      }

      try {
        const response = await permissionRolesApi.pagesAccess(
          companyId
        );
        this.pageAccess = response.data.pages || {};
        this.features = response.data.features || {};
        this.isLoaded = true;
      } catch (error) {
        console.error("Ошибка загрузки доступов к страницам:", error);
        this.isLoaded = true;
      }
    },

    async silentRefresh(router) {
      const companyId = getCompanyId();
      if (!companyId) return;

      try {
        const response = await permissionRolesApi.pagesAccess(
          companyId
        );
        const fresh = response.data.pages || {};
        const freshFeatures = response.data.features || {};

        const changed =
          JSON.stringify(fresh) !== JSON.stringify(this.pageAccess) ||
          JSON.stringify(freshFeatures) !== JSON.stringify(this.features);

        if (changed) {
          this.pageAccess = fresh;
          this.features = freshFeatures;

          if (router) {
            this._checkCurrentRoute(router);
          }
        }
      } catch {
        // тихо игнорируем ошибки сети при поллинге
      }
    },

    _checkCurrentRoute(router) {
      const currentRoute = router.currentRoute.value;
      const pageKey = PAGE_ACCESS_KEYS[currentRoute.name];

      if (pageKey && !this.canSeePage(pageKey)) {
        router.push({ name: "Forbidden" });
      }
    },

    startPolling(router) {
      this.stopPolling();
      this._pollTimer = setInterval(() => {
        this.silentRefresh(router);
      }, POLL_INTERVAL_MS);
    },

    stopPolling() {
      if (this._pollTimer) {
        clearInterval(this._pollTimer);
        this._pollTimer = null;
      }
    },

    clearPermissions() {
      this.stopPolling();
      this.pageAccess = {};
      this.features = {};
      this.isLoaded = false;
      deleteCookie("permissions");
    },
  },
});
